import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import MoleculeRadioButtonField from '@s-ui/react-molecule-radio-button-field'
import MoleculeRadioButtonGroup from '@s-ui/react-molecule-radio-button-group'

import {BASE_CLASS_NAME, fieldsToCheck} from '../../config.js'
import {usePublishMyAdWithPlateAnalyticsTrack} from '../../hooks/usePublishMyAdWithPlateAnalyticsTrack.js'
import CardSelection from '../CardSelection/index.js'
import CardVehicle from '../CardVehicle/index.js'
import Title from '../Title/index.js'

const numberOfVehiclesPerPage = 3

const VehicleSelectionSellerPlate = ({isLoading = false, km, onDenySelection, onSelectVehicle, plate, vehicles}) => {
  const {i18n} = useContext(Context)

  const {errorTypes, trackErrorPlate, trackMoreOptionsClicked, trackShowResultsVehicleData} =
    usePublishMyAdWithPlateAnalyticsTrack()

  const [vehicleSelected, setVehicleSelected] = useState()
  const [vehiclesShown, setVehiclesShown] = useState(numberOfVehiclesPerPage)
  const [showVersionNotSelectedError, setShowVersionNotSelectedError] = useState(false)

  useMount(() => trackShowResultsVehicleData({resultsNumber: vehicles.length}))

  const showSeeMoreResultsButton = vehicles.length > vehiclesShown

  const handleSeeMoreVehicles = () => {
    trackMoreOptionsClicked()
    setVehiclesShown(previousVehiclesShown => previousVehiclesShown + numberOfVehiclesPerPage)
  }

  const handleOnChange = (ev, {value}) => {
    setShowVersionNotSelectedError(false)
    setVehicleSelected(value)
  }

  const handleOnConfirm = () => {
    if (!vehicleSelected) {
      trackErrorPlate({errorType: errorTypes.versionNotSelected})
      setShowVersionNotSelectedError(true)
      return
    }

    onSelectVehicle({vehicleSelected})
  }

  const commonData = {}
  const unCommonData = {}

  fieldsToCheck.forEach(field => {
    const isTheFieldCommonInEveryVehicle = vehicles.every(vehicle => vehicle[field] === vehicles[0][field])
    if (isTheFieldCommonInEveryVehicle) {
      commonData[field] = vehicles[0][field]
      return
    }
    unCommonData[field] = true
  })

  return (
    <>
      <Title
        description={i18n.t(
          'Hemos encontrado más de un resultado para tu coche. Révisalos y elige el que se corresponda al tuyo.'
        )}
        title={i18n.t('¿Cuál de estos coches es el tuyo?')}
      />
      <div className={`${BASE_CLASS_NAME}-vehicleSelectionContainerWrapper`}>
        <div className={`${BASE_CLASS_NAME}-vehicleSelectionContainer`}>
          <div className={`${BASE_CLASS_NAME}-vehicleSelectionCardContainer`}>
            <CardVehicle
              vehicleData={{
                plate,
                km,
                imageUrlPath: vehicles[vehicleSelected || 0].imageUrlPath,
                ...commonData
              }}
            />
          </div>
          <div className={`${BASE_CLASS_NAME}-vehicleSelectionList`}>
            <MoleculeRadioButtonGroup onChange={handleOnChange} value={vehicleSelected}>
              {vehicles.slice(0, vehiclesShown).map((vehicle, index) => {
                return (
                  <MoleculeRadioButtonField
                    fullWidth
                    id={`${vehicle.versionId}-${vehicle.fuelTypeId}`}
                    key={`${vehicle.versionId}-${vehicle.fuelTypeId}`}
                    nodeLabel={<CardSelection unCommonData={unCommonData} vehicle={vehicle} />}
                    value={index.toString()}
                  />
                )
              })}
            </MoleculeRadioButtonGroup>
          </div>
        </div>
        {showSeeMoreResultsButton && (
          <div className={`${BASE_CLASS_NAME}-vehicleSelectionListButton`}>
            <AtomButton
              design={atomButtonDesigns.OUTLINE}
              isLoading={isLoading}
              onClick={handleSeeMoreVehicles}
              shape={atomButtonShapes.CIRCULAR}
            >
              {i18n.t('Ver más opciones')}
            </AtomButton>
          </div>
        )}
        {showVersionNotSelectedError && (
          <span className={`${BASE_CLASS_NAME}-vehicleSelectionErrorMessage`}>
            Debes seleccionar un resultado antes de poder continuar.
          </span>
        )}
      </div>
      <div className={`${BASE_CLASS_NAME}-buttonGroup`}>
        <div className={`${BASE_CLASS_NAME}-button`}>
          <AtomButton
            fullWidth
            isLoading={isLoading}
            onClick={handleOnConfirm}
            shape={atomButtonShapes.CIRCULAR}
            size={atomButtonSizes.LARGE}
          >
            {i18n.t('COMMON.CONTINUE')}
          </AtomButton>
        </div>
        <div className={`${BASE_CLASS_NAME}-buttonSecondary`}>
          <AtomButton
            design={atomButtonDesigns.LINK}
            disabled={isLoading}
            fullWidth
            onClick={onDenySelection}
            size={atomButtonSizes.LARGE}
          >
            {i18n.t('Mi coche no sale en la lista')}
          </AtomButton>
        </div>
      </div>
    </>
  )
}

VehicleSelectionSellerPlate.displayName = 'VehicleSelectionSellerPlate'

VehicleSelectionSellerPlate.propTypes = {
  isLoading: PropTypes.bool,
  km: PropTypes.string.isRequired,
  onDenySelection: PropTypes.func.isRequired,
  onSelectVehicle: PropTypes.func.isRequired,
  plate: PropTypes.string.isRequired,
  vehicles: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired
}

export default VehicleSelectionSellerPlate
